import styles from './SideNavBar.module.css';
import { useLocation, useNavigate } from 'react-router-dom';
import { useRecoilState } from 'recoil';
import { currentMenu as currentMenuAtom } from 'states';
import { useEffect } from 'react';

export interface NavItem {
  id: number;
  key: string;
  name: string;
  link: string[];
  icon: string;
}

export interface NavSection {
  id: number;
  title: string;
  children: NavItem[];
}

interface SideNavBarProps {
  navItems: NavSection[];
  onClickHandler?: (event: React.MouseEvent) => void;
  showIcons?: boolean;
}

export const SideNavBar = ({ navItems, onClickHandler: _onClickHandler, showIcons }: SideNavBarProps) => {
  const location = useLocation();
  const navigate = useNavigate();

  // State Management — currentMenuAtom stores a translation KEY (e.g.
  // 'NAV.DASHBOARDS'), not display text, so the navbar heading translates
  // correctly on a language switch instead of freezing at whatever text was
  // set when the menu was last chosen.
  const [, setSelectedMenu] = useRecoilState(currentMenuAtom);

  useEffect(() => {
    // On mount/refresh (no click to derive it from), find the nav item whose
    // route matches the current path and use its key — matching the
    // `.active` class logic below, but from navItems' own data instead of
    // reading rendered DOM text (which would capture whatever language was
    // active at that moment, not update on a later language switch).
    const activeItem = navItems
      .flatMap((section) => section.children)
      .find((item) => item.link.includes(location.pathname));
    if (activeItem?.key) {
      setSelectedMenu(activeItem.key);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onSideNavigation = (e: React.MouseEvent, key: string) => {
    e.preventDefault();
    setSelectedMenu(key);
  };

  return (
    <div className={`${styles.side_nav_bg} ${styles.side_nav_space} h-100`}>
      <ul className={`${styles.side_nav_list} pt-3 pb-4 list-unstyled`}>
        {navItems &&
          navItems.map((section) => (
            <div className="mb-3 mt-1" key={section.id}>
              <h4 className="px-2 px-sm-3 px-md-3 px-lg-3 px-xl-3 px-xxl-4">{section.title}</h4>
              {section.children.map((item) => (
                <li
                  className={`${
                    item.link.includes(location.pathname)
                      ? `${styles.side_nav_list_active} active`
                      : ''
                  } pt-2 pb-2 text-start px-2 px-sm-3 px-md-3 px-lg-3 px-xl-3 px-xxl-4`}
                  key={item.id}
                  onClick={(e) => {
                    navigate(item.link.length ? item.link[0] : '');
                    onSideNavigation(e, item.key);
                  }}
                >
                  {showIcons && (
                    <span className={`${styles.side_nav_icons} material-icons pe-1`}>
                      {item.icon}
                    </span>
                  )}
                  <label>{item.name}</label>
                </li>
              ))}
            </div>
          ))}
      </ul>
    </div>
  );
};
